import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Upload, X, ImageIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useFormContext } from 'react-hook-form';

export function PropertyPhotoUpload() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { setValue, watch } = useFormContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const photoBase64 = watch('photoBase64');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  React.useEffect(() => {
    if (typeof photoBase64 === 'string' && photoBase64) {
      setPreviewUrl(photoBase64);
    } else {
      setPreviewUrl(null);
    }
  }, [photoBase64]);

  const handleFileSelect = (file: File) => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: t(
          'properties.create.basicInfo.photoUpload.validation.invalidType'
        ),
        description: t(
          'properties.create.basicInfo.photoUpload.validation.invalidTypeDesc'
        ),
        variant: 'destructive',
      });
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast({
        title: t(
          'properties.create.basicInfo.photoUpload.validation.fileTooLarge'
        ),
        description: t(
          'properties.create.basicInfo.photoUpload.validation.fileTooLargeDesc'
        ),
        variant: 'destructive',
      });
      return;
    }

    // Convert file to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      setValue('photoBase64', base64String, { shouldValidate: true });
    };
    reader.onerror = () => {
      toast({
        title: t('properties.create.basicInfo.photoUpload.validation.error'),
        description: t(
          'properties.create.basicInfo.photoUpload.validation.errorDesc'
        ),
        variant: 'destructive',
      });
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) handleFileSelect(files[0]);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) handleFileSelect(files[0]);
  };

  const handleRemovePhoto = () => {
    setValue('photoBase64', null, { shouldValidate: true });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUploadClick = () => fileInputRef.current?.click();

  return (
    <div>
      <div className="space-y-2">
        <Label>
          {t('properties.create.basicInfo.photoUpload.label')}{' '}
          <span className="text-muted-foreground text-sm">
            {t('properties.create.basicInfo.photoUpload.optional')}
          </span>
        </Label>

        {previewUrl ? (
          <Card className="border-2 border-dashed border-muted-foreground/25">
            <CardContent className="p-4">
              <div className="relative group">
                <img
                  src={previewUrl}
                  alt={t('properties.create.basicInfo.photoUpload.preview')}
                  className="w-full h-48 object-cover rounded-lg"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={handleUploadClick}
                      className="bg-white/90 hover:bg-white text-black"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {t('properties.create.basicInfo.photoUpload.change')}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={handleRemovePhoto}
                      className="bg-red-500/90 hover:bg-red-500"
                    >
                      <X className="w-4 h-4 mr-2" />
                      {t('properties.create.basicInfo.photoUpload.remove')}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={cn(
              'border-2 border-dashed rounded-lg p-6 transition-colors cursor-pointer',
              dragOver
                ? 'border-primary bg-primary/5'
                : 'border-muted-foreground/25 hover:border-muted-foreground/50'
            )}
            onClick={handleUploadClick}
          >
            <div className="text-center space-y-4">
              <ImageIcon
                className={cn(
                  'mx-auto w-12 h-12',
                  dragOver ? 'text-primary' : 'text-muted-foreground'
                )}
              />
              <div className="space-y-2">
                <h3 className="font-medium">
                  {t('properties.create.basicInfo.photoUpload.dropText')}{' '}
                  <span className="text-primary underline">
                    {t('properties.create.basicInfo.photoUpload.browseText')}
                  </span>
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t(
                    'properties.create.basicInfo.photoUpload.supportedFormats'
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={handleFileInputChange}
          className="hidden"
        />
      </div>
    </div>
  );
}
